<script setup lang="ts">
import type * as openapi from '@/helpers/api/types/openapiOverrides';
import type { FieldSize } from '@/models/main';

import { computed, ref, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import _capitalize from 'lodash/capitalize';

import NoDataSelectVListItem from '@/components/items/NoDataSelectVListItem.vue';
import useAutocompleteField from '@/composables/useAutocompleteField';
import useField from '@/composables/useField';
import { fetchGetLookupCustomer } from '@/helpers/fetchers';

import listItemStyles from '@/styles/listItem.module.scss';

export interface Props {
  modelValue?: string | null;
  size?: FieldSize;
  fetchItems?: (
    params: openapi.LookupCustomerListQueryOpenapi,
  ) => Promise<string[]>;
  loading?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  size: 'medium',
  fetchItems: undefined,
  loading: false,
});
const emit = defineEmits<{
  'update:modelValue': [value: (typeof props)['modelValue']];
}>();

const fieldValue = ref(props.modelValue);
const search = ref('');
const field = ref<HTMLFormElement | null>(null);

const items = ref<string[]>([]);
const isFetching = ref(false);
const minSearchLength = 1;

function fetchWrapper(): void {
  const customerCode = search.value;
  isFetching.value = true;
  void (props.fetchItems || fetchGetLookupCustomer)({
    customer_code: customerCode,
  })
    .then((fetchedItems) => {
      if (customerCode !== search.value) return;
      items.value = fetchedItems;
    })
    .finally(() => {
      isFetching.value = false;
    });
}

const debounceFetchWrapper = useDebounceFn(fetchWrapper, 150);

function getSearchString(search?: string | null): string {
  return (search?.trim() || '').toUpperCase();
}

watch(search, (newSearch?: string | null, oldSearch?: string | null) => {
  // console.debug('fieldValue.value', fieldValue.value);
  // console.debug('newSearch', newSearch);
  // console.debug('oldSearch', oldSearch);
  if (newSearch === null && fieldValue.value) {
    fieldValue.value = '';
    return;
  }
  newSearch = getSearchString(newSearch);
  oldSearch = getSearchString(oldSearch);
  const value = getSearchString(fieldValue.value);
  if (newSearch.length >= minSearchLength) {
    if (newSearch !== oldSearch) {
      if (newSearch !== value || oldSearch !== '') {
        void debounceFetchWrapper();
      }
    }
    // } else if (newSearch === '' && oldSearch === value) {
  } else {
    items.value = [];
  }
});

useField(props, fieldValue, (newFieldValue) => {
  emit('update:modelValue', newFieldValue as (typeof props)['modelValue']);
});
const { handleEnterKeydown } = useAutocompleteField(fieldValue, search, field);

const sizeClass = computed(() => 'size' + _capitalize(props.size));
</script>

<template>
  <v-autocomplete
    ref="field"
    v-model="fieldValue"
    v-model:search="search"
    label="Customer code"
    :items="items"
    :class="[
      'g-v-text-field',
      $style.customerCodeAutocompleteField,
      $style[sizeClass],
    ]"
    variant="outlined"
    :base-color="$style.fieldBaseColor"
    :color="$style.fieldColor"
    clearable
    menu-icon="mdi-chevron-down"
    :loading="props.loading || isFetching"
    no-filter
    data-testid="customerCodeAutocompleteField"
    @keydown.enter="handleEnterKeydown"
  >
    <template #no-data>
      <NoDataSelectVListItem>
        <template v-if="!search || search.length < minSearchLength">
          Please enter {{ minSearchLength }} or more characters
        </template>
        <template v-else>No such data</template>
      </NoDataSelectVListItem>
    </template>
    <template #item="itemData">
      <v-list-item
        :class="listItemStyles.listItem"
        :active-class="listItemStyles.listItemActive"
        :ripple="false"
        v-bind="itemData.props"
      >
        <template #title="{ title }">{{ title }}</template>
      </v-list-item>
    </template>
  </v-autocomplete>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';

:export {
  fieldBaseColor: colors.$color-border-input-hovered;
  fieldColor: colors.$color-brand-800;
}

.customerCodeAutocompleteField {
  :global {
    .v-field__input {
      min-height: 40px;
      font-size: 16px;
      line-height: 22px;
      padding: 9px 16px 7px 16px;
    }
    .v-autocomplete__selection {
      margin-top: 0;
    }
    .v-field__outline {
      --v-field-border-width: 1px !important;
    }
  }
}

.sizeSmall {
  :global {
    .v-field__input {
      min-height: 36px;
      font-size: 14px;
      line-height: 20px;
    }
  }
}

.sizeLarge {
  :global {
    .v-field__input {
      min-height: 48px;
      font-size: 18px;
      line-height: 26px;
      padding: 11px 16px 9px 16px;
    }
  }
}
</style>
